import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { 
    FaSearch, 
    FaCopy, 
    FaFileExcel, 
    FaPrint, 
    FaFilePdf,
    FaArrowUp,
    FaArrowDown
} from 'react-icons/fa';

const ZpHeadTransferWorkList = () => {
    const [works, setWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // 1. Fetch Lottery Results & Work Names
        const resultsRef = ref(db, 'ekamwatapzp/LotteryResults');
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');

        onValue(resultsRef, (resultsSnapshot) => {
            const resultsData = resultsSnapshot.val() || {};
            
            onValue(worksRef, (worksSnapshot) => {
                const worksData = worksSnapshot.val() || {};
                let filteredWorks = [];

                Object.entries(worksData).forEach(([meetingNum, meetingWorks]) => {
                    if (typeof meetingWorks === 'object' && meetingWorks !== null) {
                        Object.entries(meetingWorks).forEach(([id, work]) => {
                            // Path used in LotteryResults
                            const meetingPath = meetingNum.toString().replace(/[.#$[\]]/g, "_");
                            const workPath = (work.workNumber || '').toString().replace(/[.#$[\]]/g, "_");

                            const winnerId = resultsData[meetingPath]?.[workPath];
                            
                            // Check if winner is an Engineer
                            if (winnerId && typeof winnerId === 'string' && winnerId.startsWith('Engineer_')) {
                                filteredWorks.push({
                                    id,
                                    meetingNum,
                                    ...work,
                                    winnerId
                                });
                            }
                        });
                    }
                });

                setWorks(filteredWorks.reverse());
                setLoading(false);
            });
        });

        return () => unsubscribe();
    }, [navigate]);

    const filteredData = works.filter(item => {
        const searchStr = searchTerm.toLowerCase();
        return (
            (item.workNumber || '').toLowerCase().includes(searchStr) ||
            (item.workName || '').toLowerCase().includes(searchStr) ||
            (item.taluka || '').toLowerCase().includes(searchStr) ||
            (item.schemeName || '').toLowerCase().includes(searchStr)
        );
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            {/* Breadcrumb / Top Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                    सुशिक्षित बेरोजगार अभियंता कामाची यादी
                </h1>
                <div className="text-[11px] md:text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400 font-bold">ट्रान्स्फर वर्क यादी</span>
                </div>
            </div>

            {/* Custom Styled Table Container */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                
                {/* Toolbar Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-1.5 self-start">
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Copy</button>
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Excel</button>
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Print</button>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <span className="text-sm font-bold text-gray-500">Search:</span>
                        <input 
                            type="text" 
                            className="flex-grow md:w-64 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition bg-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-y border-gray-200">
                            <tr>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center w-16">
                                    # <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    वर्क आयडी <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    टाईप <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    तालुका <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    निविदा रक्कम <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">
                                    कामाचे नाव <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest text-center">
                                    अतिरिक्त कागदपत्रे <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredData.map((item, index) => (
                                <tr key={item.id} className="hover:bg-blue-50/20 transition-all font-medium text-gray-700">
                                    <td className="px-5 py-4 border-r text-center text-sm">{index + 1}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold text-[#4e73df]">{item.workNumber || item.id.substring(0, 5)}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm">सु.बे. अभियंता</td>
                                    <td className="px-5 py-4 border-r text-center text-sm">{item.taluka}</td>
                                    <td className="px-5 py-4 border-r text-center text-sm text-red-500 font-bold">{item.tenderAmount}</td>
                                    <td className="px-5 py-4 border-r text-sm leading-relaxed">{item.workName}</td>
                                    <td className="px-5 py-4 text-center">
                                        <button 
                                            onClick={() => navigate(`/zp-head/work-info/details/${item.meetingNum}/${item.id}`)}
                                            className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md text-xs font-black transition uppercase tracking-wider"
                                        >
                                            पहा
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredData.length === 0 && (
                        <div className="py-12 text-center text-gray-400 font-bold bg-[#f8f9fa] border-b border-gray-100">
                            No data available in table
                        </div>
                    )}
                </div>

                {/* Footer Section Matching Image */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                    <div className="text-sm font-medium text-gray-500">
                        Showing {filteredData.length === 0 ? "0 to 0 of 0" : `1 to ${filteredData.length} of ${filteredData.length}`} entries
                    </div>
                    <div className="flex border border-gray-200 rounded overflow-hidden">
                        <button className="px-4 py-2 text-sm font-black text-gray-400 bg-gray-50/50 hover:bg-gray-100 disabled:opacity-50 border-r border-gray-200" disabled>Previous</button>
                        <button className="px-4 py-2 text-sm font-black text-gray-400 bg-gray-50/50 hover:bg-gray-100 disabled:opacity-50" disabled>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZpHeadTransferWorkList;
